import { createFileRoute, redirect } from '@tanstack/react-router'
import { z } from 'zod'

import { authClient } from '#features/auth/auth-service.ts'
import { DeviceVerificationPage } from '#features/auth/device-verification-page.tsx'

/**
 * Standalone `/device` route — the landing page for the OAuth 2.0 Device
 * Authorization Grant (RFC 8628) that the Trevize CLI opens as
 * `/device?user_code=…`.
 *
 * WHY it is standalone (not under `_auth` or `_app`): the device flow needs an
 * *authenticated* session so the verify call can claim the pending code for a
 * real user, but the page renders its own Trevize-branded full-screen layout
 * rather than the app chrome. `_auth` is for anonymous auth screens (and
 * bounces signed-in users away) and `_app` wraps everything in the workspace
 * shell — neither fits. So this route owns the "must be signed in, but show the
 * bare verification surface" case directly.
 *
 * An unauthenticated visitor is redirected to `/login` with `redirectTo` set to
 * the current href, which preserves the `user_code` so the CLI-supplied code
 * survives a GitHub / Linear / email sign-in and the user lands back here ready
 * to approve or deny. `location.href` is an internal path+search string, so it
 * passes `_auth`'s open-redirect guard (must start with `/`, not `//`).
 */
export const Route = createFileRoute('/device')({
  head: () => ({
    meta: [{ title: 'Authorize device' }],
  }),
  // `user_code` is validated only as an optional string here; the page's pure
  // state machine decides whether it is well-formed (jump straight to verify)
  // or missing/malformed (show the manual entry form). `.catch(undefined)`
  // keeps a hostile param (e.g. an array) from erroring the whole route.
  validateSearch: z.object({
    user_code: z.string().optional().catch(undefined),
  }),
  beforeLoad: async ({ location }) => {
    const session = await authClient.getSession()

    if (!session.data?.session || !session.data.user) {
      throw redirect({
        to: '/login',
        search: {
          redirectTo: location.href,
        },
      })
    }

    document.body.classList.add('loaded')
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { user_code } = Route.useSearch()

  return <DeviceVerificationPage userCode={user_code} />
}
